import { request as apiRequest, BaseResponse } from '../api';

// 用户状态枚举
export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  BANNED = 'banned'
}

// 用户接口定义
export interface User {
  id: string;        // 改为与后端一致的id字段
  username: string;
  email: string;
  full_name?: string;
  phone?: string;
  avatar?: string;
  is_active: boolean;
  is_admin: boolean;
  is_superuser: boolean;
  status?: UserStatus;
  roles?: Array<{
    id: string;      // 改为与后端一致的id字段
    name: string;
    description?: string;
  }>;
  last_login?: string;
  created_at: string;
  updated_at?: string;
}

// 登录数据接口
export interface LoginData {
  username: string;
  password: string;
}

// 注册数据接口
export interface RegisterData {
  username: string;
  email: string;
  password: string;
  full_name?: string;
  phone?: string;
}

// 修改密码数据接口
export interface ChangePasswordData {
  old_password: string;
  new_password: string;
}

// 重置密码数据接口
export interface ResetPasswordData {
  username: string;
  email: string;
  new_password: string;
}

// 登录响应接口
export interface LoginResponse {
  access_token: string;
  token_type: string;
  user_info?: {
    corp_id: string;
    user_id: string;
    user_name: string;
  };
  soke_jwt: string;
  oauth_code: string;
}

// 用户查询参数
export interface UserQuery {
  username?: string;
  email?: string;
  is_active?: boolean;
  page?: number;
  pageSize?: number;
}

// 用户列表响应
export interface UserListResponse {
  list: User[];
  total: number;
  current: number;
  pageSize: number;
}

/**
 * 用户登录
 */
export function login(data: LoginData): Promise<BaseResponse<LoginResponse>> {
  return apiRequest<LoginResponse>('/auth/login', {
    method: 'POST',
    data,
  });
}

/**
 * SSO单点登录
 */
export function ssoLogin(code: string): Promise<BaseResponse<LoginResponse>> {
  return apiRequest<LoginResponse>('/auth/sso-login', {
    method: 'POST',
    params: { code },
  });
}

/**
 * 钉钉免登
 */
export function dingTalkLogin(authCode: string, corpId: string): Promise<BaseResponse<LoginResponse>> {
  return apiRequest<LoginResponse>('/auth/dingtalk-login', {
    method: 'POST',
    data: { 
      authCode,
      corpId 
    },
  });
}

/**
 * 用户注册
 */
export function register(data: RegisterData): Promise<BaseResponse<{ user_id: string }>> {
  return apiRequest<{ user_id: string }>('/auth/register', {
    method: 'POST',
    data,
  });
}

/**
 * 获取当前用户信息
 */
export function getUserInfo(): Promise<BaseResponse<User>> {
  return apiRequest<User>('/users/me');
}

/**
 * 获取用户列表
 */
export function getUsersList(params?: UserQuery): Promise<BaseResponse<UserListResponse>> {
  return apiRequest<UserListResponse>('/users', {
    method: 'GET',
    params,
  });
}

/**
 * 更新用户信息
 */
export function updateUserInfo(data: Partial<Pick<User, 'full_name' | 'phone'>>): Promise<BaseResponse<User>> {
  return apiRequest<User>('/users/me', {
    method: 'PUT',
    data,
  });
}

/**
 * 用户退出登录
 */
export function logout(): Promise<BaseResponse<{ message: string }>> {
  return apiRequest<{ message: string }>('/auth/logout', {
    method: 'POST',
  });
}

/**
 * 修改密码
 */
export function changePassword(data: ChangePasswordData): Promise<BaseResponse<{ message: string }>> {
  return apiRequest<{ message: string }>('/auth/change-password', {
    method: 'POST',
    data,
  });
}

/**
 * 重置密码（忘记密码）
 */
export function resetPassword(data: ResetPasswordData): Promise<BaseResponse<{ message: string }>> {
  return apiRequest<{ message: string }>('/auth/reset-password', {
    method: 'POST',
    data,
  });
}